import { CalendarOff, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { useMeusFeriados } from '@/hooks/useMeusFeriados';
import { useAtuacao } from '@/lib/delegacao/atuacao';
import { classifyError } from '@/lib/errors';
import { FERIADOS_PAGE } from '@/pages/feriados/rota';

function contagem(n: number, um: string, varios: string): string {
  return `${n} ${n === 1 ? um : varios}`;
}

/**
 * Resumo dos feriados e suspensões lançados na conta, em Configurações. Não lista nem
 * desliga — mostra quantos estão ligados e em análise e leva à página dedicada.
 */
export function MeusFeriadosSummaryCard() {
  const navigate = useNavigate();
  const atuacao = useAtuacao();
  const { data, isLoading, error } = useMeusFeriados();

  const ligados = data?.filter((f) => f.ativo).length ?? 0;
  // Em análise: o admin ainda não decidiu. Desligada sai da fila dele (PLANO_CONTAGEM_PRAZOS, Bloco D).
  const emAnalise = data?.filter((f) => f.ativo && f.sugestao === 'pendente').length ?? 0;

  return (
    <Card className="border bg-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <CalendarOff className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">
            {atuacao ? `Feriados e suspensões da conta de ${atuacao.titularNome}` : 'Meus feriados e suspensões'}
          </CardTitle>
        </div>
        <CardDescription>Os dias que a conta do prazo não pegou, lançados nesta conta</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Spinner size={32} label="Carregando feriados e suspensões" />
          </div>
        ) : (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            {/* Com erro, o resumo avisa, mas a página continua a um toque: ela tem o próprio estado. */}
            {error ? (
              <Alert variant="destructive" className="sm:flex-1">
                <AlertDescription>{classifyError(error).message}</AlertDescription>
              </Alert>
            ) : data?.length ? (
              <div className="space-y-0.5">
                <p className="text-sm font-medium">{contagem(ligados, 'ligado', 'ligados')}</p>
                {emAnalise > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {emAnalise} em análise para valer para todos
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Nenhum lançado.</p>
            )}
            <Button variant="outline" className="min-h-11 w-full sm:min-h-10 sm:w-auto" onClick={() => navigate(FERIADOS_PAGE)}>
              Abrir
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
